import React, { FC, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Modal, Spinner } from 'react-bootstrap';
import {
  WhatsAppBridgeSession,
  GetWhatsAppBridgeSessionsResponse,
  GetWhatsAppBridgeQRResponse,
} from '@dreamai/types';
import { ReactComponent as WhatsappIcon } from '../../icons/whatsapp.svg';
import { ReactComponent as BoltIcon } from '../../icons/bolt.svg';
import { usePusher } from '../../contexts/PusherContext';
import { useAgent } from '../../contexts/AgentContext';
import api from '../../api';
import './whatsapp-bridge-card.scss';

type CardState =
  | 'loading'
  | 'not_connected'
  | 'consent'
  | 'pairing'
  | 'connected'
  | 'error'
  | 'disconnecting';

const QR_POLL_INTERVAL_MS = 500;
const MAX_CONSECUTIVE_POLL_ERRORS = 5;

const CircleMinusIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <circle cx='12' cy='12' r='9' stroke='currentColor' strokeWidth='1.5' />
    <line
      x1='8'
      y1='12'
      x2='16'
      y2='12'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
    />
  </svg>
);

const WhatsAppBridgeCard: FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const pusherChannel = usePusher();

  const { agents } = useAgent();
  const assistantName =
    agents.find((a) => (a.agent_type as string) === 'Personal Assistant')
      ?.nickname || 'your Assistant';

  const [cardState, setCardState] = useState<CardState>('loading');
  const [session, setSession] = useState<WhatsAppBridgeSession | null>(null);
  const [qrData, setQrData] = useState<GetWhatsAppBridgeQRResponse | null>(
    null,
  );
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [showPairingModal, setShowPairingModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [feedback, setFeedback] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollStoppedRef = useRef<boolean>(false);
  const pairingSessionRef = useRef<string | null>(null);
  const consecutiveErrorsRef = useRef<number>(0);

  const clearPolling = useCallback(() => {
    pollStoppedRef.current = true;
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const fetchSessions = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<GetWhatsAppBridgeSessionsResponse>(
        '/api/whatsapp-bridge/sessions',
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const activeSessions = res.data.sessions.filter(
        (s: WhatsAppBridgeSession) => s.status === 'connected',
      );
      if (activeSessions.length > 0) {
        setSession(activeSessions[0]);
        setCardState('connected');
      } else {
        setSession(null);
        setCardState('not_connected');
      }
    } catch (err) {
      console.error('Error fetching bridge sessions:', err);
      setCardState('not_connected');
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Pusher event listeners
  useEffect(() => {
    if (!pusherChannel) return;

    const handleConnected = (_data: { sessionId: string }) => {
      clearPolling();
      setShowPairingModal(false);
      fetchSessions();
      setFeedback({
        type: 'success',
        message: 'WhatsApp connected!',
      });
      setTimeout(() => setFeedback(null), 3000);
    };

    const handleDisconnected = () => {
      setSession(null);
      setCardState('not_connected');
      setFeedback({
        type: 'success',
        message: 'WhatsApp disconnected.',
      });
      setTimeout(() => setFeedback(null), 3000);
    };

    pusherChannel.bind('whatsapp-bridge:connected', handleConnected);
    pusherChannel.bind('whatsapp-bridge:disconnected', handleDisconnected);

    return () => {
      pusherChannel.unbind('whatsapp-bridge:connected', handleConnected);
      pusherChannel.unbind('whatsapp-bridge:disconnected', handleDisconnected);
    };
  }, [pusherChannel, clearPolling, fetchSessions]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => clearPolling();
  }, [clearPolling]);

  const handleConnect = useCallback(() => {
    setShowConsentModal(true);
  }, []);

  const handleConsentAccept = useCallback(async () => {
    setShowConsentModal(false);
    setShowPairingModal(true);
    setQrData(null);
    setCardState('pairing');

    try {
      const token = await getAccessTokenSilently();
      const res = await api.post<WhatsAppBridgeSession>(
        '/api/whatsapp-bridge/sessions',
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const newSession = res.data;
      pairingSessionRef.current = newSession.id;

      // Start sequential polling for QR code (wait for each poll to
      // complete before scheduling the next to avoid overlapping requests)
      consecutiveErrorsRef.current = 0;
      pollStoppedRef.current = false;
      const pollQr = async () => {
        if (pollStoppedRef.current) return;
        try {
          const qrRes = await api.get<GetWhatsAppBridgeQRResponse>(
            `/api/whatsapp-bridge/sessions/${newSession.id}/qr`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          consecutiveErrorsRef.current = 0;
          setQrData(qrRes.data);

          if (qrRes.data.status === 'connected') {
            clearPolling();
            setShowPairingModal(false);
            fetchSessions();
            return;
          }

          if (qrRes.data.status === 'error') {
            clearPolling();
            setShowPairingModal(false);
            setErrorDetail(qrRes.data.error || 'Unknown pairing error');
            setShowErrorModal(true);
            setCardState('error');
            return;
          }

          if (
            qrRes.data.status === 'closed' ||
            qrRes.data.status === 'timeout'
          ) {
            clearPolling();
            setShowPairingModal(false);
            setErrorDetail('qr_expired');
            setShowErrorModal(true);
            setCardState('error');
            return;
          }
        } catch (err) {
          consecutiveErrorsRef.current += 1;
          console.error(
            `QR poll error (${consecutiveErrorsRef.current}/${MAX_CONSECUTIVE_POLL_ERRORS}):`,
            err,
          );
          if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_POLL_ERRORS) {
            clearPolling();
            setShowPairingModal(false);
            setErrorDetail('service_unavailable');
            setShowErrorModal(true);
            setCardState('error');
            return;
          }
        }
        if (!pollStoppedRef.current) {
          pollRef.current = setTimeout(pollQr, QR_POLL_INTERVAL_MS);
        }
      };

      pollQr();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      console.error('Error creating bridge session:', err);
      setShowPairingModal(false);
      setCardState('not_connected');
      setFeedback({
        type: 'error',
        message:
          axiosErr?.response?.data?.error ||
          'Failed to start pairing. Please try again.',
      });
      setTimeout(() => setFeedback(null), 5000);
    }
  }, [getAccessTokenSilently, clearPolling, fetchSessions]);

  const handleDisconnect = useCallback(async () => {
    if (!session) return;
    setCardState('disconnecting');

    try {
      const token = await getAccessTokenSilently();
      await api.delete(`/api/whatsapp-bridge/sessions/${session.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSession(null);
      setCardState('not_connected');
      setFeedback({
        type: 'success',
        message: 'WhatsApp disconnected.',
      });
      setTimeout(() => setFeedback(null), 3000);
    } catch (err) {
      console.error('Error disconnecting bridge:', err);
      setCardState('connected');
      setFeedback({
        type: 'error',
        message: 'Failed to deactivate. Please try again.',
      });
      setTimeout(() => setFeedback(null), 5000);
    }
  }, [session, getAccessTokenSilently]);

  const handleClosePairingModal = useCallback(() => {
    clearPolling();
    setShowPairingModal(false);
    setCardState('not_connected');
  }, [clearPolling]);

  return (
    <div className='wa-bridge-column'>
      <BoltIcon className='wa-column-icon bolt-icon' />
      <h4 className='wa-column-title'>
        Let {assistantName} check your WhatsApp
      </h4>
      <p className='wa-column-description'>
        Let {assistantName} read and reply to messages for you
      </p>

      {feedback && (
        <p className={`wa-bridge-feedback ${feedback.type}`}>
          {feedback.message}
        </p>
      )}

      {cardState === 'loading' && <div className='settings-skeleton' />}

      {cardState === 'connected' && session && (
        <div className='wa-connected-row'>
          <div className='wa-connected-info'>
            <WhatsappIcon className='wa-connected-icon' />
            <span className='wa-connected-label'>WhatsApp connected</span>
          </div>
          <button
            className='wa-disconnect-btn'
            onClick={handleDisconnect}
            aria-label='Disconnect WhatsApp'
          >
            <CircleMinusIcon />
          </button>
        </div>
      )}

      {cardState === 'disconnecting' && (
        <div className='wa-connected-row'>
          <div className='wa-connected-info'>
            <Spinner animation='border' size='sm' />
            <span className='wa-connected-label'>Deactivating...</span>
          </div>
        </div>
      )}

      {(cardState === 'not_connected' || cardState === 'error') && (
        <button className='wa-activate-btn' onClick={handleConnect}>
          Connect my WhatsApp
        </button>
      )}

      {/* Consent Modal */}
      <Modal
        show={showConsentModal}
        onHide={() => setShowConsentModal(false)}
        centered
        className='wa-bridge-consent-modal'
      >
        <Modal.Header closeButton>
          <Modal.Title>Connect my WhatsApp</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='wa-consent-body'>
            <div className='wa-consent-illustration'>
              <svg
                width='64'
                height='64'
                viewBox='0 0 64 64'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <rect
                  x='16'
                  y='8'
                  width='32'
                  height='48'
                  rx='4'
                  stroke='#374562'
                  strokeWidth='2'
                />
                <rect
                  x='24'
                  y='14'
                  width='16'
                  height='2'
                  rx='1'
                  fill='#374562'
                />
                <circle cx='32' cy='50' r='2' fill='#374562' />
                <rect
                  x='22'
                  y='22'
                  width='20'
                  height='20'
                  rx='3'
                  stroke='#128c7e'
                  strokeWidth='1.5'
                />
                <path
                  d='M28 32 L30 34 L36 28'
                  stroke='#128c7e'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <rect
                  x='38'
                  y='26'
                  width='12'
                  height='16'
                  rx='2'
                  stroke='#374562'
                  strokeWidth='1.5'
                  fill='white'
                />
                <circle cx='44' cy='38' r='1.5' fill='#374562' />
                <rect
                  x='41'
                  y='29'
                  width='6'
                  height='1'
                  rx='0.5'
                  fill='#374562'
                />
              </svg>
            </div>
            <p className='wa-consent-text'>
              Your life is on WhatsApp, so let your Assistant manage it for you.
              They&apos;ll find when your friends booked lunch, or coordinate
              your group trip. See which messages aren&apos;t replied to, and
              include them in your daily briefing.
            </p>
            <p className='wa-consent-text'>
              They can even reply for you, with your permission.
            </p>
            <div className='wa-consent-security-box'>
              <div className='wa-consent-lock-icon'>
                <svg
                  width='20'
                  height='20'
                  viewBox='0 0 20 20'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <rect
                    x='4'
                    y='9'
                    width='12'
                    height='9'
                    rx='2'
                    stroke='#128c7e'
                    strokeWidth='1.5'
                  />
                  <path
                    d='M7 9V6a3 3 0 0 1 6 0v3'
                    stroke='#128c7e'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                  />
                </svg>
              </div>
              <p className='wa-consent-security-text'>
                Your messages are stored with the digital equivalent of an
                unbreakable vault &mdash; AES-256-GCM encryption. Dream staff
                can&apos;t read your messages, and all data is deleted after 6
                months. Revoke access anytime.
              </p>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            className='wa-consent-cancel-btn'
            onClick={() => setShowConsentModal(false)}
          >
            Cancel
          </button>
          <button
            className='wa-consent-activate-btn'
            onClick={handleConsentAccept}
          >
            <WhatsappIcon className='wa-consent-btn-icon' />
            <span>Yes, connect</span>
          </button>
        </Modal.Footer>
      </Modal>

      {/* Pairing Modal */}
      <Modal
        show={showPairingModal}
        onHide={handleClosePairingModal}
        centered
        className='wa-bridge-pairing-modal'
      >
        <Modal.Header closeButton>
          <Modal.Title>Connect my WhatsApp</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='wa-pairing-body'>
            {!qrData || qrData.status === 'waiting' ? (
              <div className='wa-pairing-loading'>
                <Spinner animation='border' size='sm' />
                <p>Preparing QR code...</p>
              </div>
            ) : qrData.status === 'ready' || qrData.status === 'scanned' ? (
              <div className='wa-pairing-content'>
                {qrData.qrCode && (
                  <img
                    src={`data:image/png;base64,${qrData.qrCode}`}
                    alt='WhatsApp QR code'
                    className='wa-pairing-qr-image'
                  />
                )}
                {qrData.pairingCode && (
                  <p className='wa-pairing-code'>
                    Pairing code: <strong>{qrData.pairingCode}</strong>
                  </p>
                )}
                <div className='wa-pairing-instructions'>
                  <p>
                    <strong>To pair:</strong>
                  </p>
                  <ol>
                    <li>Open WhatsApp on your phone</li>
                    <li>
                      Tap <strong>&#8942;</strong> (More) &rarr;{' '}
                      <strong>Linked Devices</strong> &rarr;{' '}
                      <strong>Link a Device</strong>
                    </li>
                    <li>Scan this QR code with your phone camera</li>
                  </ol>
                  {qrData.pairingCode && (
                    <p className='wa-pairing-alt'>
                      Or enter the pairing code above when prompted.
                    </p>
                  )}
                </div>
                {qrData.status === 'scanned' && (
                  <div className='wa-pairing-scanned'>
                    <Spinner animation='border' size='sm' />
                    <span>QR scanned — completing connection...</span>
                  </div>
                )}
              </div>
            ) : qrData.status === 'closed' || qrData.status === 'timeout' ? (
              <div className='wa-pairing-loading'>
                <p>QR code expired. Please close and try again.</p>
              </div>
            ) : null}
          </div>
        </Modal.Body>
      </Modal>

      {/* Error Modal */}
      <Modal
        show={showErrorModal}
        onHide={() => {
          setShowErrorModal(false);
          setCardState('not_connected');
        }}
        centered
        className='wa-bridge-error-modal'
      >
        <Modal.Header closeButton>
          <Modal.Title>Pairing Failed</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='wa-error-body'>
            {errorDetail === 'device_limit_reached' ? (
              <>
                <p>
                  <strong>Device limit reached.</strong> WhatsApp allows a
                  maximum of 4 linked devices.
                </p>
                <p>
                  To connect Dream, unlink one of your existing devices first:
                </p>
                <ol>
                  <li>Open WhatsApp on your phone</li>
                  <li>
                    Tap <strong>&#8942;</strong> (More) &rarr;{' '}
                    <strong>Linked Devices</strong>
                  </li>
                  <li>Tap a linked device and select &quot;Log out&quot;</li>
                  <li>Come back here and try connecting again</li>
                </ol>
              </>
            ) : errorDetail === 'qr_expired' ? (
              <>
                <p>
                  <strong>QR code expired.</strong> The pairing session timed
                  out before a QR code could be displayed.
                </p>
                <p>
                  This can happen if the bridge service is busy. Please try
                  again.
                </p>
              </>
            ) : errorDetail === 'service_unavailable' ? (
              <>
                <p>
                  <strong>Connection service unavailable.</strong> The WhatsApp
                  bridge service could not be reached.
                </p>
                <p>
                  This is usually temporary. Please try again in a few minutes.
                  If the problem persists, contact support.
                </p>
              </>
            ) : (
              <p>
                {errorDetail ||
                  'An error occurred during pairing. Please try again.'}
              </p>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            className='wa-consent-activate-btn'
            onClick={() => {
              setShowErrorModal(false);
              setCardState('not_connected');
            }}
          >
            OK
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default WhatsAppBridgeCard;
